/**
 * On-screen debug log (core/debug-log.ts): a corner button that counts the
 * errors this tab has hit, a panel listing them newest-first, and "Send to
 * owner", which posts the log with a note to POST /api/debug-reports for the
 * project owner to read on the admin page.
 *
 * Mounted once in App.tsx, OUTSIDE the router, so it is on every screen —
 * dashboard, board, admin — and survives navigation between them (the ring
 * is per tab, not per screen: the error that broke the board is often the
 * one logged on the dashboard a moment earlier).
 */
import { DEBUG_REPORT_MAX_NOTE, type DebugReportContext, type DebugReportRequest } from "@kola/shared";
import { useRef, useState } from "react";
import { useStore } from "zustand";

import { sendDebugReport } from "../../core/api.js";
import {
  boardIdFromPath,
  clearDebugLog,
  clientVersion,
  debugConnection,
  debugLogStore,
  formatDebugLog,
  openDebugLog,
  toDto,
  type DebugLogEntry,
} from "../../core/debug-log.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { Modal } from "../../ui/Modal.js";
import { showToast } from "../../ui/toast.js";
import { DebugEntryLine } from "./DebugEntryLine.js";
import { getHost } from "../../core/host.js";
import { routeStore } from "../../core/route-state.js";
import { serverInfoStore } from "../../core/server-info.js";

const bugIcon = (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 7V6a3 3 0 0 1 6 0v1" />
    <path d="M6 10a6 6 0 0 1 12 0v4a6 6 0 0 1-12 0z" />
    <path d="M12 9v11M3 13h3M18 13h3M4.5 20l2.5-2M19.5 20l-2.5-2M4.5 7.5l2.5 2M19.5 7.5l-2.5 2" />
  </svg>
);

/**
 * What the report says about this tab, read at send/copy time. The route
 * comes from the router (core/route-state.ts), not `window.location`: inside
 * the desktop app the URL is the host's and says nothing about Kola. The
 * shell and the versions on both ends are what turn "works on the web, not
 * in the app" into something the owner can read off the report.
 */
export function reportContext(): DebugReportContext {
  const host = getHost();
  const route = routeStore.getState();
  const { serverVersion } = serverInfoStore.getState();
  return {
    url: host === null ? window.location.href : `embedded:${route.path}`,
    userAgent: navigator.userAgent,
    version: clientVersion(),
    boardId: route.boardId ?? boardIdFromPath(window.location.pathname),
    connection: debugConnection(),
    shell: host === null ? "web" : "embedded",
    ...(host?.hostVersion === undefined ? {} : { hostVersion: host.hostVersion }),
    ...(serverVersion === null ? {} : { serverVersion }),
  };
}

export function DebugLogPanel() {
  const entries = useStore(debugLogStore, (s) => s.entries);
  const unseen = useStore(debugLogStore, (s) => s.unseen);
  const open = useStore(debugLogStore, (s) => s.open);
  const [sending, setSending] = useState(false);

  const errorCount = entries.filter((entry) => entry.level === "error").length;
  // Closed: how many errors arrived since it was last opened. Open: the total.
  const badge = open ? errorCount : unseen > 0 ? unseen : errorCount;

  const copy = async () => {
    const text = formatDebugLog(entries, reportContext());
    try {
      await navigator.clipboard.writeText(text);
      showToast("Debug log copied");
    } catch {
      showToast("Couldn't copy — your browser blocked clipboard access");
    }
  };

  return (
    <>
      <button
        type="button"
        className={`debug-log-toggle${errorCount > 0 ? " has-errors" : ""}${open ? " open" : ""}`}
        onClick={() => openDebugLog(!open)}
        aria-label={
          errorCount > 0
            ? `Debug log — ${errorCount} error${errorCount === 1 ? "" : "s"}`
            : "Debug log"
        }
        aria-expanded={open}
        data-tip="Debug log"
      >
        {bugIcon}
        {badge > 0 && <span className="debug-log-count">{badge > 99 ? "99+" : badge}</span>}
      </button>

      {open && (
        <section className="debug-log-panel" aria-label="Debug log">
          <header className="debug-log-head">
            <strong>Debug log</strong>
            <span className="debug-log-meta">
              {entries.length} {entries.length === 1 ? "line" : "lines"} · {errorCount}{" "}
              {errorCount === 1 ? "error" : "errors"} · v{clientVersion()}
            </span>
            <div className="spacer" />
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void copy()}
              disabled={entries.length === 0}
            >
              Copy
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearDebugLog}
              disabled={entries.length === 0}
            >
              Clear
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setSending(true)}>
              Send to owner
            </button>
            <CloseButton onClick={() => openDebugLog(false)} />
          </header>
          {entries.length === 0 ? (
            <div className="debug-log-empty">
              Nothing logged in this tab yet. Errors, failed requests and connection changes
              will appear here as they happen.
            </div>
          ) : (
            <ol className="debug-log-list">
              {[...entries].reverse().map((entry) => (
                <DebugEntryLine key={entry.id} entry={entry} />
              ))}
            </ol>
          )}
        </section>
      )}

      {sending && <SendDebugReportDialog entries={entries} onClose={() => setSending(false)} />}
    </>
  );
}

export function SendDebugReportDialog({ entries, onClose, initialNote = "", context }: {
  entries: readonly DebugLogEntry[];
  onClose: () => void;
  initialNote?: string;
  context?: DebugReportContext;
}) {
  const [note, setNote] = useState(initialNote.slice(0, DEBUG_REPORT_MAX_NOTE));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const trimmed = note.trim();
    const body: DebugReportRequest = {
      ...(trimmed ? { note: trimmed } : {}),
      context: context ?? reportContext(),
      entries: entries.map(toDto),
    };
    try {
      const result = await sendDebugReport(body);
      showToast(`Report sent to the developer — ${result.id}`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? `Couldn't send the report: ${err.message}` : "Couldn't send the report");
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal title="Send report to developer" onClose={() => { if (!inFlight.current) onClose(); }}>
      <p className="debug-send-hint">
        Sends the {entries.length} {entries.length === 1 ? "line" : "lines"} in this tab's log,
        the page URL, your browser and the Kola version to Admin → Debug reports.
        Add anything that will help the developer reproduce the problem.
      </p>
      <textarea
        className="text-input debug-send-note"
        rows={4}
        maxLength={DEBUG_REPORT_MAX_NOTE}
        placeholder="What happened? e.g. the board has said “Connecting…” since 10:20"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        autoFocus
      />
      {error !== null && <p className="error-note" role="alert">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </Modal>
  );
}
